import { useState, useMemo, useEffect } from 'react';
import { Trophy, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import type { CornerData, CornerLapResult } from '@/types/telemetry';
import type { CornerPhase } from './CornerPhaseTabs';
import type { CornerMetric } from './CornerMetricTabs';
import { formatTime } from '@/lib/format';
import { formatDynamicCornerLabel } from '@/lib/spline';
import { useI18n } from '@/context/I18nContext';
import { Pagination } from '@/components/common/Pagination';

interface CornerLapTableProps {
  corner: CornerData;
  activePhase: CornerPhase;
  activeMetric?: CornerMetric;
}

type SortField = 'metric' | 'lapTime' | 'lapNumber' | 'driver' | 'date' | 'delta';
type SortOrder = 'asc' | 'desc';

const PAGE_SIZE = 10;

export function CornerLapTable({ corner, activePhase, activeMetric = 'speed' }: CornerLapTableProps) {
  const { t } = useI18n();
  const [sortField, setSortField] = useState<SortField>('metric');
  const [sortOrder, setSortOrder] = useState<SortOrder>(activeMetric === 'duration' ? 'asc' : 'desc');
  const [page, setPage] = useState(1);

  const laps = corner.laps || [];

  // Reset page and default sort order when corner, active phase, or active metric changes
  useEffect(() => {
    setPage(1);
    setSortField('metric');
    setSortOrder(activeMetric === 'duration' ? 'asc' : 'desc');
  }, [corner.cornerId, corner.orderIndex, activePhase, activeMetric]);

  // Helper to extract speed for active phase
  const getPhaseSpeed = (lap: CornerLapResult, phase: CornerPhase): number => {
    switch (phase) {
      case 'entrance': return lap.entrySpeedKmh;
      case 'apex': return lap.apexSpeedKmh;
      case 'exit': return lap.exitSpeedKmh;
    }
  };

  // Helper to extract RPM for active phase
  const getPhaseRpm = (lap: CornerLapResult, phase: CornerPhase): number => {
    switch (phase) {
      case 'entrance': return lap.entryRpm ?? 0;
      case 'apex': return lap.apexRpm ?? 0;
      case 'exit': return lap.exitRpm ?? 0;
    }
  };

  const bestDurationMs = corner.durationStats?.minDurationMs ?? 0;

  const activeMaxSpeed =
    activePhase === 'entrance'
      ? corner.entryStats?.maxSpeedKmh ?? 0
      : activePhase === 'apex'
        ? corner.apexStats?.maxSpeedKmh ?? 0
        : corner.exitStats?.maxSpeedKmh ?? 0;

  const activeMaxRpm =
    activePhase === 'entrance'
      ? corner.entryRpmStats?.maxRpm ?? 0
      : activePhase === 'apex'
        ? corner.apexRpmStats?.maxRpm ?? 0
        : corner.exitRpmStats?.maxRpm ?? 0;

  const getMetricValue = (lap: CornerLapResult): number => {
    if (activeMetric === 'rpm') return getPhaseRpm(lap, activePhase);
    if (activeMetric === 'duration') return lap.durationMs ?? 0;
    return getPhaseSpeed(lap, activePhase);
  };

  const getDeltaValue = (lap: CornerLapResult): number => {
    if (activeMetric === 'rpm') return lap.rpmDelta ?? 0;
    if (activeMetric === 'duration') return lap.durationDeltaMs ?? 0;
    return lap.speedDeltaKmh;
  };

  const sortedLaps = useMemo(() => {
    return [...laps].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'metric':
          cmp = getMetricValue(a) - getMetricValue(b);
          break;
        case 'lapTime':
          cmp = a.lapTimeMs - b.lapTimeMs;
          break;
        case 'lapNumber':
          cmp = a.lapNumber - b.lapNumber;
          break;
        case 'driver':
          cmp = (a.driverName || '').localeCompare(b.driverName || '');
          break;
        case 'date':
          cmp = `${a.sessionDate} ${a.sessionTime}`.localeCompare(`${b.sessionDate} ${b.sessionTime}`);
          break;
        case 'delta':
          cmp = getDeltaValue(a) - getDeltaValue(b);
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [laps, sortField, sortOrder, activePhase, activeMetric]);

  const totalPages = Math.max(1, Math.ceil(sortedLaps.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedLaps = sortedLaps.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      if (field === 'lapTime' || (field === 'metric' && activeMetric === 'duration') || (field === 'delta' && activeMetric === 'duration')) {
        setSortOrder('asc');
      } else {
        setSortOrder('desc');
      }
    }
    setPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={11} className="text-text-muted opacity-40 group-hover:opacity-100 ml-1 inline" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp size={11} className="text-accent ml-1 inline" />
      : <ChevronDown size={11} className="text-accent ml-1 inline" />;
  };

  const phaseLabel = activeMetric === 'duration'
    ? t('corner.fullTransit')
    : activeMetric === 'rpm'
      ? (activePhase === 'entrance' ? t('corner.entranceRpm') : activePhase === 'apex' ? t('corner.apexRpm') : t('corner.exitRpm'))
      : (activePhase === 'entrance' ? t('corner.entranceSpeed') : activePhase === 'apex' ? t('corner.apexSpeed') : t('corner.exitSpeed'));

  if (laps.length === 0) {
    return (
      <div className="card p-6 text-center text-text-muted text-sm">
        {t('common.noData')}
      </div>
    );
  }

  return (
    <div className="card p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5 pb-4 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            {formatDynamicCornerLabel(corner.name, corner.orderIndex, t('corner.turn') || 'Turn')} · {phaseLabel}
          </span>
          <span className="text-xs text-text-muted mono hidden sm:inline">
            ({laps.length} {t('corner.lapsAnalyzed')})
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <span>{t('corner.activeMetric')}</span>
          <span className="font-semibold text-text-primary capitalize">
            {activeMetric === 'duration' ? t('corner.duration') : `${t(`corner.metric${activeMetric.charAt(0).toUpperCase() + activeMetric.slice(1)}` as any)} · ${t(`corner.${activePhase}`)}`}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px] border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-center py-2.5 px-3 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted w-12 sticky left-0 bg-bg-surface z-10 border-r border-border-subtle">
                {t('corner.rank')}
              </th>
              <th
                onClick={() => handleSort('driver')}
                className="text-left py-2.5 px-4 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors group"
              >
                <span>{t('corner.driver')}</span>
                {getSortIcon('driver')}
              </th>
              <th
                onClick={() => handleSort('date')}
                className="text-left py-2.5 px-4 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors hidden sm:table-cell group"
              >
                <span>{t('corner.session')}</span>
                {getSortIcon('date')}
              </th>
              <th
                onClick={() => handleSort('lapNumber')}
                className="text-center py-2.5 px-3 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors w-16 group"
              >
                <span>{t('corner.lap')}</span>
                {getSortIcon('lapNumber')}
              </th>
              <th
                onClick={() => handleSort('lapTime')}
                className="text-right py-2.5 px-4 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors group"
              >
                <span>{t('corner.lapTime')}</span>
                {getSortIcon('lapTime')}
              </th>

              {/* Metric Column */}
              <th
                onClick={() => handleSort('metric')}
                className="text-right py-2.5 px-4 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors group"
              >
                <span>
                  {activeMetric === 'rpm' ? t('corner.rpm') : activeMetric === 'duration' ? t('corner.duration') : t('corner.speed')}
                </span>
                {getSortIcon('metric')}
              </th>

              {/* Delta / Gain Column */}
              <th
                onClick={() => handleSort('delta')}
                className="text-right py-2.5 px-4 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors group"
              >
                <span>
                  {activeMetric === 'rpm' ? t('corner.rpmGain') : activeMetric === 'duration' ? t('corner.deltaToBestDuration') : t('corner.speedGain')}
                </span>
                {getSortIcon('delta')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedLaps.map((lap, idx) => {
              const globalRank = (currentPage - 1) * PAGE_SIZE + idx + 1;

              let isBest = false;
              if (activeMetric === 'speed') {
                const spd = getPhaseSpeed(lap, activePhase);
                isBest = activeMaxSpeed > 0 && Math.abs(spd - activeMaxSpeed) < 0.05;
              } else if (activeMetric === 'rpm') {
                const rpm = getPhaseRpm(lap, activePhase);
                isBest = activeMaxRpm > 0 && rpm === activeMaxRpm;
              } else if (activeMetric === 'duration') {
                const dur = lap.durationMs ?? 0;
                isBest = bestDurationMs > 0 && dur === bestDurationMs;
              }

              return (
                <tr
                  key={`${lap.sessionId}-${lap.lapNumber}`}
                  className={`hover:bg-bg-hover transition-colors ${
                    idx < paginatedLaps.length - 1 ? 'divider' : ''
                  } ${
                    isBest ? 'bg-yellow/10' : ''
                  }`}
                >
                  <td className="py-2.5 px-3 text-center mono font-medium text-text-secondary sticky left-0 bg-bg-surface z-10 border-r border-border-subtle">
                    {isBest ? (
                      <span className="inline-flex items-center justify-center gap-0.5 text-yellow font-bold text-xs">
                        <Trophy size={11} /> {globalRank}
                      </span>
                    ) : (
                      <span className="text-xs text-text-muted">{globalRank}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-text-primary font-medium">
                    {lap.driverName || '—'}
                  </td>
                  <td className="py-2.5 px-4 text-xs text-text-secondary hidden sm:table-cell">
                    {lap.sessionDate} <span className="text-text-muted text-[11px]">{lap.sessionTime}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center mono text-text-secondary">
                    L{lap.lapNumber}
                  </td>
                  <td className="py-2.5 px-4 text-right mono font-medium text-text-primary">
                    {formatTime(lap.lapTimeMs)}
                  </td>

                  {/* Primary Metric value */}
                  <td className="py-2.5 px-4 text-right mono font-semibold text-text-primary">
                    {activeMetric === 'speed' && (
                      <>
                        {getPhaseSpeed(lap, activePhase).toFixed(1)} <span className="text-[11px] font-normal text-text-muted">km/h</span>
                      </>
                    )}
                    {activeMetric === 'rpm' && (
                      <>
                        {getPhaseRpm(lap, activePhase).toLocaleString()} <span className="text-[11px] font-normal text-text-muted">RPM</span>
                      </>
                    )}
                    {activeMetric === 'duration' && (
                      <>
                        {((lap.durationMs ?? 0) / 1000).toFixed(3)} <span className="text-[11px] font-normal text-text-muted">s</span>
                      </>
                    )}
                  </td>

                  {/* Secondary Delta / Gain value */}
                  <td className="py-2.5 px-4 text-right mono font-semibold text-xs">
                    {activeMetric === 'speed' && (
                      <span className={lap.speedDeltaKmh >= 0 ? 'text-green' : 'text-red'}>
                        {lap.speedDeltaKmh >= 0 ? `+${lap.speedDeltaKmh.toFixed(1)}` : lap.speedDeltaKmh.toFixed(1)}
                      </span>
                    )}
                    {activeMetric === 'rpm' && (
                      <span className={(lap.rpmDelta ?? 0) >= 0 ? 'text-green' : 'text-red'}>
                        {(lap.rpmDelta ?? 0) >= 0 ? `+${(lap.rpmDelta ?? 0).toLocaleString()}` : (lap.rpmDelta ?? 0).toLocaleString()}
                      </span>
                    )}
                    {activeMetric === 'duration' && (
                      (() => {
                        const delta = (lap.durationDeltaMs ?? 0) / 1000;
                        if (delta === 0) {
                          return <span className="text-green font-bold">0.000 s</span>;
                        }
                        return <span className="text-text-muted">+{delta.toFixed(3)} s</span>;
                      })()
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
